import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
} from '@mui/material';
import {
  enum2NumberArray,
  intentionallyFloatingPromiseReturn,
} from 'core/utils/commonUtils';
import { getOrderActionName } from 'core/utils/orderUtils';
import { OrderActionEnum } from 'models/enums/orderActionEnum';
import { useEffect, useMemo, type FC } from 'react';
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form';
import DatePickerElement from './DatePickerElement';
import TableToolbar from './TableToolbar';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
    },
  },
};

const OrdersTableToolbar: FC<OrdersTableToolbarProps> = ({
  onFilterChange,
  initFilters,
  filtersVisibility = {},
}) => {
  const defaultValues = useMemo<OrdersTableToolbarFilter>(
    () => ({
      actions: initFilters?.actions ?? [],
      fromCreateDate: initFilters?.fromCreateDate ?? null,
      toCreateDate: initFilters?.toCreateDate ?? null,
      fromChangeStatusToOrderDate: initFilters?.fromChangeStatusToOrderDate ?? null,
      toChangeStatusToOrderDate: initFilters?.toChangeStatusToOrderDate ?? null,
    }),
    [initFilters]
  );

  const methods = useForm<OrdersTableToolbarFilter>({
    defaultValues,
  });

  const { handleSubmit, reset } = methods;

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const applyFiltersHandler = handleSubmit((data) => {
    onFilterChange?.(data);
  });

  const closeFiltersHandler = (): void => {
    reset(defaultValues);
  };

  const Filter: FC = () => {
    const { control } = useFormContext<OrdersTableToolbarFilter>();

    return (
      <Stack gap={1}>
        {/* Фильтр по этапам (actions) */}
        {filtersVisibility.actions !== false && (
          <FormControl fullWidth size='small'>
            <InputLabel id='action-label'>Этап</InputLabel>
            <Controller
              name='actions'
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  labelId='action-label'
                  id='action-filter'
                  multiple
                  input={<OutlinedInput label='Этап' />}
                  MenuProps={MenuProps}
                  renderValue={(selected) => selected.map(getOrderActionName).join(', ')}>
                  {enum2NumberArray(OrderActionEnum).map((a) => (
                    <MenuItem key={a} value={a}>
                      <Checkbox checked={field.value?.includes(a)} />
                      <ListItemText primary={getOrderActionName(a)} />
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </FormControl>
        )}

        {/* Фильтры по дате создания заявки */}
        {filtersVisibility.fromCreateDate !== false && (
          <DatePickerElement
            name='fromCreateDate'
            control={control}
            placeholder='Дата создания, от'
          />
        )}

        {filtersVisibility.toCreateDate !== false && (
          <DatePickerElement
            name='toCreateDate'
            control={control}
            placeholder='Дата создания, до'
          />
        )}

        {/* Фильтры по дате создания заказа */}
        {filtersVisibility.fromChangeStatusToOrderDate !== false && (
          <DatePickerElement
            name='fromChangeStatusToOrderDate'
            control={control}
            placeholder='Дата создания, от'
          />
        )}

        {filtersVisibility.toChangeStatusToOrderDate !== false && (
          <DatePickerElement
            name='toChangeStatusToOrderDate'
            control={control}
            placeholder='Дата создания, до'
          />
        )}
      </Stack>
    );
  };

  return (
    <FormProvider {...methods}>
      <TableToolbar
        FilterComponent={Filter}
        onApplyFilters={intentionallyFloatingPromiseReturn(applyFiltersHandler)}
        onCloseFilters={closeFiltersHandler}
      />
    </FormProvider>
  );
};

export interface OrdersTableToolbarFilter {
  actions?: number[];
  fromCreateDate?: Date | null;
  toCreateDate?: Date | null;
  fromChangeStatusToOrderDate?: Date | null;
  toChangeStatusToOrderDate?: Date | null;
}

export interface OrdersTableToolbarFilterVisibility {
  actions: boolean;
  fromCreateDate: boolean;
  toCreateDate: boolean;
  fromChangeStatusToOrderDate: boolean;
  toChangeStatusToOrderDate: boolean;
}

export interface OrdersTableToolbarProps {
  onFilterChange?: (filter: OrdersTableToolbarFilter) => void;
  initFilters?: OrdersTableToolbarFilter;
  filtersVisibility?: Partial<OrdersTableToolbarFilterVisibility>;
}

export default OrdersTableToolbar;
